<template>
  <section ref="rootEl" class="founder-section" :class="{ visible: isVisible }">
    <div id="OurStory" class="founder-anchor"></div>

    <div class="founder-inner">
      <!-- Eyebrow -->
      <div class="founder-eyebrow">FROM THE FOUNDER</div>

      <!-- Pull quote — the heart of the section -->
      <blockquote class="founder-quote">
        "Life is too short for bad dinners and wasted weekends. AI should be the
        friend that knows you well enough to make sure every choice is the right
        one."
      </blockquote>

      <!-- Founder identity row -->
      <div class="founder-ident">
        <img
          src="@/assets/images/our-story-photo.jpg"
          alt="Tavishi, founder of Tilly"
          class="founder-photo"
        />
        <div class="founder-meta">
          <div class="founder-name">Tavishi</div>
          <div class="founder-role">Founder, Tilly</div>
        </div>
      </div>

      <!-- Short note -->
      <p class="founder-note">
        The best parts of life, at least for me, have always come from
        experiences - the places you didn't plan for, the dinners that turn into
        longer nights, the moments you keep thinking about years later. And what
        I've realized is that the difference between a good experience and a
        forgettable one is usually something small: a choice. Where you go, what
        you pick, what you miss. Too often, that choice gets shaped by the wrong
        review, someone else’s taste, or just not having the time (or energy) to
        figure it out. I spent three years traveling and building Tern, an AI
        travel planner used by people in 120 countries. It worked. But what kept
        coming up, over and over again, was this: people didn't just want help
        for big trips - they wanted it in their everyday lives. So we rebuilt
        it. This is Tilly.
      </p>

      <!-- Manifesto CTA — read the full piece on Substack -->
      <a
        href="https://travelwithtern.substack.com/p/the-explorers-exhale"
        target="_blank"
        rel="noopener noreferrer"
        class="manifesto-btn"
      >
        Read our manifesto
        <span class="manifesto-btn-arrow" aria-hidden="true">→</span>
      </a>

      <!-- Tern nudge — quiet, inline -->
      <div class="tern-nudge">
        Used Tern before?
        <router-link to="/travel" class="tern-link"
          >Your account still works →</router-link
        >
      </div>
    </div>
  </section>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount } from 'vue';

const rootEl = ref(null);
const isVisible = ref(false);
let observer = null;

onMounted(() => {
  if (!('IntersectionObserver' in window) || !rootEl.value) {
    isVisible.value = true;
    return;
  }
  observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) {
          isVisible.value = true;
          observer.unobserve(e.target);
        }
      });
    },
    { threshold: 0.15 },
  );
  observer.observe(rootEl.value);
});

onBeforeUnmount(() => {
  if (observer) observer.disconnect();
});
</script>

<style scoped>
/* ─── Section shell ──────────────────────────────────────── */
.founder-section {
  width: 100%;
  padding: clamp(4rem, 8vw, 7rem) clamp(1.25rem, 5vw, 3rem);
  background: #ffffff;
  display: flex;
  justify-content: center;
  margin-bottom: -1px;
  position: relative;
  z-index: 2;
}

.founder-anchor {
  position: absolute;
  top: -80px;
  height: 0;
  visibility: hidden;
}

/* ─── Content column — editorial width ───────────────────── */
.founder-inner {
  max-width: 640px;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 1.75rem;
}

/* ─── Eyebrow ────────────────────────────────────────────── */
.founder-eyebrow {
  font-family: 'AirbnbCereal_W_Md', -apple-system, sans-serif;
  font-size: 0.62rem;
  letter-spacing: 0.28em;
  text-transform: uppercase;
  color: #94a3b8;
  opacity: 0;
  transform: translateY(10px);
  transition: opacity 0.6s ease 0.05s, transform 0.6s ease 0.05s;
}
.visible .founder-eyebrow {
  opacity: 1;
  transform: translateY(0);
}

/* ─── Pull quote ─────────────────────────────────────────── */
.founder-quote {
  margin: 0;
  font-family: 'AirbnbCereal_W_Bd', -apple-system, sans-serif;
  font-size: clamp(1.4rem, 3.5vw, 2rem);
  font-weight: 800;
  line-height: 1.25;
  letter-spacing: -0.025em;
  color: #0f172a;
  font-style: italic;
  opacity: 0;
  transform: translateY(16px);
  transition: opacity 0.75s cubic-bezier(0.22, 0.61, 0.36, 1) 0.1s,
    transform 0.75s cubic-bezier(0.22, 0.61, 0.36, 1) 0.1s;
}
.visible .founder-quote {
  opacity: 1;
  transform: translateY(0);
}

/* ─── Founder identity ───────────────────────────────────── */
.founder-ident {
  display: flex;
  align-items: center;
  gap: 0.85rem;
  opacity: 0;
  transform: translateY(12px);
  transition: opacity 0.65s ease 0.2s, transform 0.65s ease 0.2s;
}
.visible .founder-ident {
  opacity: 1;
  transform: translateY(0);
}

.founder-photo {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
  border: 2px solid rgba(0, 0, 0, 0.06);
}

.founder-name {
  font-family: 'AirbnbCereal_W_Bd', -apple-system, sans-serif;
  font-size: 0.95rem;
  font-weight: 700;
  color: #0f172a;
  line-height: 1.2;
}
.founder-role {
  font-family: 'AirbnbCereal_W_Bk', -apple-system, sans-serif;
  font-size: 0.78rem;
  color: #94a3b8;
  margin-top: 0.1rem;
}

/* ─── Short note ─────────────────────────────────────────── */
.founder-note {
  font-family: 'AirbnbCereal_W_Bk', -apple-system, sans-serif;
  font-size: clamp(0.95rem, 1.4vw, 1.05rem);
  line-height: 1.72;
  color: #475569;
  margin: 0;
  opacity: 0;
  transform: translateY(12px);
  transition: opacity 0.7s ease 0.28s, transform 0.7s ease 0.28s;
}
.visible .founder-note {
  opacity: 1;
  transform: translateY(0);
}

/* ─── Manifesto CTA — glass pill, mirrors the floating "Save my spot" ─ */
.manifesto-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.65rem 1.25rem 0.65rem 1.4rem;
  border-radius: 9999px;
  font-family: 'AirbnbCereal_W_Bd', -apple-system, sans-serif;
  font-weight: 700;
  font-size: 0.95rem;
  letter-spacing: 0.01em;
  color: #ffffff;
  text-decoration: none;
  background: linear-gradient(135deg, #58a8e1 0%, #4ba8c9 100%);
  border: 1px solid rgba(255, 255, 255, 0.35);
  box-shadow: 0 8px 24px rgba(88, 168, 225, 0.32),
    0 2px 6px rgba(15, 42, 65, 0.08), inset 0 1px 0 rgba(255, 255, 255, 0.25);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  opacity: 0;
  transition: opacity 0.65s ease 0.33s,
    transform 0.25s cubic-bezier(0.34, 1.56, 0.64, 1), box-shadow 0.25s ease;
}
.visible .manifesto-btn {
  opacity: 1;
}
.manifesto-btn:hover {
  transform: translateY(-2px) scale(1.03);
  box-shadow: 0 12px 32px rgba(88, 168, 225, 0.42),
    0 4px 10px rgba(15, 42, 65, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.35);
}
.manifesto-btn:active {
  transform: translateY(-1px) scale(1);
}
.manifesto-btn-arrow {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.25s cubic-bezier(0.34, 1.56, 0.64, 1);
}
.manifesto-btn:hover .manifesto-btn-arrow {
  transform: translateX(3px);
}

/* ─── Tern nudge ─────────────────────────────────────────── */
.tern-nudge {
  font-family: 'AirbnbCereal_W_Bk', -apple-system, sans-serif;
  font-size: 0.88rem;
  color: #94a3b8;
  opacity: 0;
  transform: translateY(10px);
  transition: opacity 0.6s ease 0.38s, transform 0.6s ease 0.38s;
}
.visible .tern-nudge {
  opacity: 1;
  transform: translateY(0);
}

.tern-link {
  color: #b45309;
  text-decoration: none;
  font-family: 'AirbnbCereal_W_Md', sans-serif;
  margin-left: 0.3em;
  transition: opacity 0.2s ease;
}
.tern-link:hover {
  opacity: 0.75;
}
</style>
